import React from "react";
import { Link } from "react-router-dom";

function LegalShell({ title, children, testId }) {
  return (
    <div className="min-h-screen bg-[#0A051A] text-[#D6C9F0] px-6 py-16" data-testid={testId}>
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-[#00F0FF] font-mono text-xs tracking-[0.3em] hover:underline">
          ← BACK
        </Link>
        <h1 className="font-heading font-black text-4xl neon-pink mt-6 mb-8 uppercase tracking-wider">
          {title}
        </h1>
        <div className="font-mono text-sm leading-relaxed space-y-4 text-[#A89BCC]">
          {children}
        </div>
        <div className="mt-16 text-[#4A3B70] font-mono text-[10px] tracking-widest">
          © 2026 RING KINGS · NEON FEDERATION
        </div>
      </div>
    </div>
  );
}


export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" testId="privacy-policy-page">
      <h2>Privacy Policy</h2>
      <p>
        This policy explains what personal data RING KINGS collects,
        how it is used, and what rights you have.
      </p>

      <h3>1. Controller</h3>
      <p>
        Jan Schlosser, Dunckerstraße 15, 10437 Berlin, Germany.
        E-mail: wrestlinggamenetwork@gmail.com. See also our Legal Notice.
      </p>

      <h3>2. What We Process, Why, and Legal Basis</h3>

      <p>
        <strong>a) Sign-in with Google.</strong> Login uses Google Sign-In
        through the authentication service of our hosting provider, Emergent
        Labs. Google sends us your email address, name, profile picture, and
        Google account ID. We store only your email address and Google account
        ID. Legal basis: Art. 6(1)(b) GDPR.
      </p>

      <p>
        <strong>b) Login session.</strong> A strictly necessary cookie,
        session_token (HttpOnly, Secure), keeps you signed in for 7 days.
        The token is also stored on our servers. Legal basis: Art. 6(1)(b)
        GDPR and Section 25(2) no. 2 TDDDG.
      </p>

      <p><strong>c) Game data.</strong> This includes:</p>
      <ul>
        <li>Your account name and your wrestlers' names, stats, levels, items, and currencies (Hot Dogs, Handshakes, and Fans).</li>
        <li>Achievements, cards, PvP and show results, promotion and tag-team memberships, and World Tour progress.</li>
        <li>Your referral code and who referred you, your last online time (for the online indicator and player counts), and tutorial progress.</li>
      </ul>
      <p>Legal basis: Art. 6(1)(b) GDPR.</p>

      <p>
        <strong>d) Publicly visible to other players.</strong> Your account
        name, avatars, wrestler names, promotion, rankings, match results,
        and online status.
      </p>

      <p>
        <strong>e) Uploaded images.</strong> Avatars you upload are converted
        to WebP and stored in cloud storage run by Emergent Labs. Only your
        latest image per wrestler, manager, or promotion is kept. Legal basis:
        Art. 6(1)(b) GDPR.
      </p>

      <p>
        <strong>f) Chat and private messages.</strong> Chat messages are
        visible to other players, and only the latest 200 are kept. Private
        messages are stored to provide the in-game messaging feature. They
        remain stored while the account is active. If you request account
        deletion, your private messages associated with the account will be
        deleted as part of that process.
      </p>

      <p>
        <strong>g) Purchases.</strong> Payments go through Stripe Payments
        Europe Ltd. (Dublin) using Stripe Managed Payments. Stripe may act
        as the seller of record and handles VAT. We never receive card data.
        We store the checkout session ID, your user ID, the package, amount,
        currency, and payment status. Legal basis: Art. 6(1)(b) and (c) GDPR.
        {" "}
        <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
          Stripe Privacy Policy
        </a>.
      </p>

      <p>
        <strong>h) Hosting and technical data.</strong> RING KINGS is hosted
        by Emergent Labs, Inc. The hosting provider processes data necessary
        to operate and secure the game, which may include technical connection
        data such as IP addresses, request timestamps, and browser information.
        The provider's own data processing and retention practices are subject
        to its applicable privacy policy and contractual arrangements.
      </p>

      <p>
        <strong>i) Data stored on your device.</strong> Your browser keeps a
        pending referral code during sign-up, flags for pop-ups and boss
        introductions you've already seen, and an image cache to speed up
        loading. This data stays on your device. Legal basis: Section 25(2)
        no. 2 TDDDG.
      </p>

      <h3>3. Recipients</h3>
      <p>RING KINGS uses third-party services to run the game:</p>
      <ul>
        <li><strong>Emergent Labs:</strong> Hosting, database, file storage, and login.</li>
        <li><strong>Google:</strong> Google sign-in.</li>
        <li><strong>Stripe:</strong> Payments.</li>
      </ul>
      <p>
        We use Cloudflare Web Analytics to understand overall website traffic and
        improve the game. It provides aggregated usage statistics and is designed
        not to use cookies or track individual users across websites.
      </p>
      <p>
        These services may process personal data as needed to provide their
        services. Data may be processed outside the EU.
      </p>
      <p>We do not sell your personal data or use it for advertising.</p>

      <h3>4. Transfers Outside the EU</h3>
      <p>
        Some of our service providers may process personal data outside the
        European Union, including in the United States. Where required, such
        transfers must comply with applicable data protection laws.
      </p>

      <h3>5. Data Retention</h3>
      <ul>
        <li><strong>Account and game data:</strong> Kept until your account is deleted.</li>
        <li><strong>Login sessions:</strong> Session tokens expire after 7 days. Expired session records are automatically deleted after they have been expired for more than one hour.</li>
        <li><strong>Public chat:</strong> Only the latest 200 messages are kept.</li>
        <li><strong>Private messages:</strong> Kept while needed to provide the in-game messaging service.</li>
        <li><strong>Payment records:</strong> Kept for as long as required by applicable law.</li>
        <li><strong>Avatars:</strong> The latest uploaded image is kept for each wrestler, manager, and promotion. Replaced images are overwritten. Removed images may remain in storage until replaced by a new upload.</li>
      </ul>

      <h3>6. Your Rights</h3>
      <p>
        You can exercise your data protection rights by contacting us using
        the email address listed in our Legal Notice. Depending on the
        circumstances, these rights include accessing, correcting, or deleting
        your personal data, restricting or objecting to certain processing,
        and receiving a copy of your data. Where processing is based on
        consent, you can withdraw that consent.
      </p>

      <h3>7. Account Deletion</h3>
      <p>
        Request account deletion via{" "}
        <a href="mailto:wrestlinggamenetwork@gmail.com">
          wrestlinggamenetwork@gmail.com
        </a>
        . Payment records we must legally keep are retained until that period
        ends.
      </p>

      <h3>8. Age Requirement, Purchases by Minors, and Refunds</h3>
      <p>RING KINGS is intended for users aged 16 and over.</p>

      <h4>Purchases by Minors</h4>
      <p>
        Players under 18 should obtain permission from a parent or legal
        guardian before making real-money purchases in RING KINGS.
      </p>

      <h4>Refunds</h4>
      <p>
        If you believe a minor has made a purchase without the required
        permission, please contact us using the email address in our Legal
        Notice. Include the purchase receipt and the player's account name so
        we can review the request.
      </p>
      <p>
        We will review refund requests in accordance with applicable law.
        Nothing in this policy limits your statutory consumer rights.
      </p>

      <h3>9. Automated Decisions</h3>
      <p>
        RING KINGS does not make automated decisions within the meaning of
        Art. 22 GDPR. Match results are determined by game mechanics.
      </p>

      <h3>10. Changes</h3>
      <p>This policy may be updated as the game changes.</p>
      <p>Last updated: 25.10.2026.</p>
    </LegalShell>
  );
}


export function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" testId="terms-of-service-page">
      <h2>Step Into the Ring</h2>
      <p>Welcome to <strong>Ring Kings</strong>. Step through the ropes, manage your roster, enter the shows, and build your wrestling empire.</p>
      <p>Before you enter the ring, there are a few rules every manager needs to know. By creating an account or using Ring Kings, you agree to these Terms of Service.</p>

      <h2>Know the Rules</h2>
      <p>Every great wrestling promotion needs rules. Ring Kings is no different.</p>
      <p>You must not:</p>
      <ul>
        <li>use bots, scripts, macros, or other unauthorized automation;</li>
        <li>exploit bugs or unintended game mechanics;</li>
        <li>manipulate matches, rankings, events, or other game systems;</li>
        <li>interfere with the operation of the game;</li>
        <li>abuse payment or purchase systems;</li>
        <li>intentionally circumvent technical or account restrictions.</li>
      </ul>
      <p>If you discover an exploit or serious bug, please report it rather than turning it into your personal championship advantage.</p>

      <h2>Keep It Civil</h2>
      <p>The Ring Kings arena is a place for competition, rivalry, trash talk, and wrestling fun — but there are still lines you don't cross.</p>
      <p>Harassment, threats, spam, impersonation, doxxing and other abusive or unlawful behaviour are not permitted.</p>
      <p>We may remove content and restrict or suspend accounts where necessary to protect players, the community, or the operation of the game.</p>

      <h2>What's in Your Corner?</h2>
      <p>Ring Kings contains virtual currencies and game resources, including <strong>Hot Dogs, Handshakes, and Fans</strong>, as well as virtual items, gear, cards, titles, and other progression.</p>
      <p>These are virtual game resources only. They have no real-world monetary value, cannot be exchanged for real money, and cannot be transferred between players unless a game feature explicitly allows it.</p>
      <p>Game currencies, items, rewards, progression systems, events, and other content may be changed, rebalanced, or discontinued as Ring Kings develops.</p>

      <h2>The Business End</h2>
      <p>Some virtual items or currencies in Ring Kings may be available for purchase with real money.</p>
      <p>The package contents, price, and applicable VAT are displayed during Stripe checkout before you complete your purchase. Payments are processed through Stripe Managed Payments.</p>
      <p>All purchases are subject to applicable consumer protection laws. Nothing in these Terms limits your statutory consumer rights.</p>
      
      <h2>The Show Must Go On</h2>
      <p>Ring Kings is an evolving online game. We may add, remove, modify, rebalance, or discontinue features, rewards, currencies, progression systems, events, or other game content.</p>
      <p>Like any online show, Ring Kings may occasionally go off the air for maintenance, updates, technical problems, or circumstances outside our control.</p>
      <p>We do not guarantee that the game will always be available or operate without interruption.</p>

      <h2>Ejected from the Arena</h2>
      <p>Cheating, exploiting, fraud, serious abuse, payment abuse, or significant violations of these Terms may result in an account being restricted, suspended, or terminated.</p>
      <p>Where appropriate, players may contact us regarding an account restriction.</p>
      <p>We reserve the right to take immediate action where necessary to protect Ring Kings, its players, or its operation.</p>

      <h2>Who Owns the Ring?</h2>
      <p>Ring Kings, including its software, branding, original artwork, interface, text, and other game content, is protected by applicable intellectual property laws.</p>
      <p>You may not reproduce, distribute, commercially exploit, or otherwise use Ring Kings content outside the permitted use of the game without permission.</p>
      <p>Your account and game progress do not give you ownership of the underlying game, software, virtual items, or intellectual property.</p>

      <h2>The Backstage Area</h2>
      <p>Your privacy matters. Information about how personal data is collected and processed is provided in the <strong>Privacy Policy</strong>.</p>

      <h2>Need to Talk to Management?</h2>
      <p>If you have a question about Ring Kings, these Terms, your account, or something happening in the arena, you can contact us using the information provided in the <strong>Legal Notice</strong>.</p>

      <h2>The Legal Fine Print</h2>
      <p>Nothing in these Terms is intended to limit any mandatory rights that apply to you under applicable consumer protection law.</p>

      <p><strong>Now step through the ropes, manage your roster, and let the show begin.</strong></p>
    </LegalShell>
  );
}

export function LegalNotice() {
  return (
    <LegalShell title="Legal Notice" testId="legal-notice-page">
      <h2>Information according to § 5 DDG</h2>

      <p>
        <strong>Operator:</strong><br />
        Jan Schlosser
      </p>

      <p>
        <strong>Address:</strong><br />
        Dunckerstraße 15<br />
        10437 Berlin<br />
        Germany
      </p>

      <p>
        <strong>Contact:</strong><br />
        Email: wrestlinggamenetwork@gmail.com
      </p>
    </LegalShell>
  );
}
